import * as React from "react"
import { GripHorizontalIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import type { DialogDragHandleProps } from "@/hooks/useDraggableDialog"

/**
 * Lets a dialog header act as the drag surface for the dialog that contains it.
 * Dialogs and alert dialogs share one context: they are never meaningfully
 * nested, and the nearest provider is always the right one.
 */
const DialogDragContext = React.createContext<DialogDragHandleProps | null>(null)

export const DialogDragProvider = DialogDragContext.Provider

/** Drag handlers for the surrounding dialog, or null when it is not draggable. */
export function useDialogDragHandle() {
  return React.useContext(DialogDragContext)
}

/** Classes that turn a header into the pointer drag surface. */
export const DIALOG_DRAG_SURFACE_CLASS = "cursor-move touch-none"

/**
 * Keyboard route to the same movement, hidden until focused so it never covers
 * the dialog's own header content.
 */
function DialogMoveHandle({
  slot,
  className,
  handleProps,
  onReset,
}: {
  slot: string
  className?: string
  handleProps: DialogDragHandleProps
  onReset: () => void
}) {
  return (
    <button
      type="button"
      data-slot={slot}
      aria-label="Move dialog with the arrow keys"
      onKeyDown={handleProps.onKeyDown}
      onPointerDown={handleProps.onPointerDown}
      onDoubleClick={onReset}
      className={cn(
        "ring-offset-background focus-visible:ring-ring sr-only focus-visible:not-sr-only focus-visible:absolute focus-visible:top-4 focus-visible:z-10 focus-visible:rounded-xs focus-visible:bg-background focus-visible:p-1 focus-visible:text-muted-foreground focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden",
        className
      )}
    >
      <GripHorizontalIcon className="size-4" aria-hidden="true" />
    </button>
  )
}

export { DialogMoveHandle }
